/**
 * CreateCollectionModal — admin modal for creating a new draft collection.
 *
 * Mirrors the shape of `CreateTaxonomyModal` (controlled `open` /
 * `onOpenChange`, manual validation, reset on close, isPending button
 * state). Calls `useCreateCollection`, which surfaces success/409/error
 * toasts; on success this navigates to the new collection's detail page.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '../../../../../ui/button';
import { Checkbox } from '../../../../../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../ui/dialog';
import { Input } from '../../../../../ui/input';
import { Label } from '../../../../../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../ui/select';
import { Textarea } from '../../../../../ui/textarea';

import { useLiveVersion, useTaxonomyKeys } from '../../../hooks/useTaxonomy';
import { CHANNELS, type ChannelKey } from '../../../shared/channels';
import { useCreateCollection } from '../../hooks/useDatasets';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type FieldErrors = Partial<{
  name: string;
  industry: string;
  region: string;
  taxonomyKey: string;
  dimensions: string;
  channels: string;
}>;

const CHANNEL_KEYS = Object.keys(CHANNELS) as ChannelKey[];

const CreateCollectionModal: React.FC<Props> = ({ open, onOpenChange }) => {
  const createCollection = useCreateCollection();

  const [name, setName] = useState('');
  const [industry, setIndustry] = useState('');
  const [region, setRegion] = useState('');
  const [description, setDescription] = useState('');
  const [taxonomyKey, setTaxonomyKey] = useState('');
  const [dimensions, setDimensions] = useState<string[]>([]);
  const [channels, setChannels] = useState<string[]>([]);
  const [errors, setErrors] = useState<FieldErrors>({});

  const { data: taxonomyKeys, isLoading: keysLoading } = useTaxonomyKeys();
  const { data: liveVersion, isLoading: liveLoading } = useLiveVersion(taxonomyKey);

  // If exactly one taxonomy exists, auto-select it — there's no choice to make.
  // Otherwise the user must pick explicitly.
  useEffect(() => {
    if (!taxonomyKey && taxonomyKeys && taxonomyKeys.length === 1) {
      setTaxonomyKey(taxonomyKeys[0].taxonomyKey);
    }
  }, [taxonomyKey, taxonomyKeys]);

  const availableDimensions = useMemo(
    () => liveVersion?.taxonomyData?.dimensions ?? [],
    [liveVersion],
  );

  // Reseed the dimension selection only when the *taxonomy choice*
  // changes — never when the live-version query refetches (which
  // produces a new object identity but the same set of dimensions).
  // Tracking the last-seeded `taxonomyKey` in a ref prevents
  // background refetches from wiping the user's hand-curated
  // checkboxes.
  //
  // We defer reseeding while the new live-version query is still
  // loading, but we use the hook's `liveLoading` flag rather than
  // inferring from `availableDimensions.length === 0`. Inferring would
  // conflate "still loading" with "loaded-but-the-taxonomy-has-zero-
  // dimensions", and the latter is a legitimate state for a freshly-
  // created taxonomy. Without this fix, switching to such a taxonomy
  // would leave the previous taxonomy's selected dimension keys in
  // form state (hidden because the new taxonomy has no checkboxes to
  // re-render them), and submission would fail with an unexpected
  // "invalid dimension" error.
  const lastSeededTaxonomyKey = useRef<string>('');
  useEffect(() => {
    if (lastSeededTaxonomyKey.current === taxonomyKey) return;
    if (taxonomyKey && liveLoading) return;
    lastSeededTaxonomyKey.current = taxonomyKey;
    setDimensions(availableDimensions.map((d) => d.id));
  }, [taxonomyKey, availableDimensions, liveLoading]);

  const resetForm = () => {
    setName('');
    setIndustry('');
    setRegion('');
    setDescription('');
    setTaxonomyKey('');
    setDimensions([]);
    setChannels([]);
    setErrors({});
    lastSeededTaxonomyKey.current = '';
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  const toggleDimension = (key: string, checked: boolean) => {
    setDimensions((prev) =>
      checked ? [...prev, key] : prev.filter((k) => k !== key),
    );
    setErrors((e) => ({ ...e, dimensions: undefined }));
  };

  const toggleChannel = (key: string, checked: boolean) => {
    setChannels((prev) =>
      checked ? [...prev, key] : prev.filter((k) => k !== key),
    );
    setErrors((e) => ({ ...e, channels: undefined }));
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const next: FieldErrors = {};
    if (!name.trim()) next.name = 'Name is required.';
    if (!industry.trim()) next.industry = 'Industry is required.';
    if (!region.trim()) next.region = 'Region is required.';
    if (!taxonomyKey) next.taxonomyKey = 'Select a taxonomy.';
    if (dimensions.length === 0) next.dimensions = 'Select at least one dimension.';
    if (channels.length === 0) next.channels = 'Select at least one channel.';
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    createCollection.mutate(
      {
        name: name.trim(),
        industry: industry.trim(),
        region: region.trim(),
        description: description.trim() || null,
        taxonomyKey,
        dimensions,
        channels,
      },
      {
        onSuccess: () => {
          resetForm();
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Create New Collection</DialogTitle>
          <DialogDescription>
            Group annotation data by industry × region. The collection is pinned
            to the live version of the chosen taxonomy.
          </DialogDescription>
        </DialogHeader>

        <form id="create-collection-form" onSubmit={handleSubmit} className="space-y-4 py-2">
          <div>
            <Label htmlFor="collection-name">
              Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="collection-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setErrors((er) => ({ ...er, name: undefined }));
              }}
              placeholder="e.g. UK Insurance"
              className="mt-1"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="collection-industry">
                Industry <span className="text-red-500">*</span>
              </Label>
              <Input
                id="collection-industry"
                value={industry}
                onChange={(e) => {
                  setIndustry(e.target.value);
                  setErrors((er) => ({ ...er, industry: undefined }));
                }}
                placeholder="e.g. Insurance"
                className="mt-1"
              />
              {errors.industry && (
                <p className="mt-1 text-sm text-red-600">{errors.industry}</p>
              )}
            </div>
            <div>
              <Label htmlFor="collection-region">
                Region <span className="text-red-500">*</span>
              </Label>
              <Input
                id="collection-region"
                value={region}
                onChange={(e) => {
                  setRegion(e.target.value);
                  setErrors((er) => ({ ...er, region: undefined }));
                }}
                placeholder="e.g. United Kingdom"
                className="mt-1"
              />
              {errors.region && (
                <p className="mt-1 text-sm text-red-600">{errors.region}</p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="collection-description">Description</Label>
            <Textarea
              id="collection-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional — a short description of this collection."
              rows={3}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="collection-taxonomy">
              Taxonomy <span className="text-red-500">*</span>
            </Label>
            <Select
              value={taxonomyKey}
              onValueChange={(v) => {
                setTaxonomyKey(v);
                setErrors((er) => ({ ...er, taxonomyKey: undefined }));
              }}
              disabled={keysLoading}
            >
              <SelectTrigger id="collection-taxonomy" className="mt-1">
                <SelectValue
                  placeholder={
                    keysLoading ? 'Loading taxonomies…' : 'Select a taxonomy'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {(taxonomyKeys ?? []).map((k) => (
                  <SelectItem key={k.taxonomyKey} value={k.taxonomyKey}>
                    {k.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.taxonomyKey && (
              <p className="mt-1 text-sm text-red-600">{errors.taxonomyKey}</p>
            )}
          </div>

          <div>
            <Label>
              Dimensions <span className="text-red-500">*</span>
            </Label>
            <div
              className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2"
              data-testid="dimensions-checkboxes"
            >
              {!taxonomyKey && (
                <p className="text-xs text-muted-foreground">
                  Select a taxonomy first.
                </p>
              )}
              {taxonomyKey && liveLoading && (
                <p className="text-xs text-muted-foreground">Loading dimensions…</p>
              )}
              {taxonomyKey &&
                !liveLoading &&
                availableDimensions.map((d) => {
                  const checked = dimensions.includes(d.id);
                  const checkboxId = `dim-${d.id}`;
                  return (
                    <label
                      key={d.id}
                      htmlFor={checkboxId}
                      className="flex cursor-pointer items-center gap-2 text-sm"
                    >
                      <Checkbox
                        id={checkboxId}
                        checked={checked}
                        onCheckedChange={(c) => toggleDimension(d.id, c === true)}
                      />
                      <span>{d.name || d.id}</span>
                    </label>
                  );
                })}
              {taxonomyKey &&
                !liveLoading &&
                availableDimensions.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    This taxonomy has no dimensions defined.
                  </p>
                )}
            </div>
            {errors.dimensions && (
              <p className="mt-1 text-sm text-red-600">{errors.dimensions}</p>
            )}
          </div>

          <div>
            <Label>
              Channels <span className="text-red-500">*</span>
            </Label>
            <div
              className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2"
              data-testid="channels-checkboxes"
            >
              {CHANNEL_KEYS.map((k) => {
                const checked = channels.includes(k);
                const checkboxId = `chan-${k}`;
                return (
                  <label
                    key={k}
                    htmlFor={checkboxId}
                    className="flex cursor-pointer items-center gap-2 text-sm"
                  >
                    <Checkbox
                      id={checkboxId}
                      checked={checked}
                      onCheckedChange={(c) => toggleChannel(k, c === true)}
                    />
                    <span>{CHANNELS[k].name}</span>
                  </label>
                );
              })}
            </div>
            {errors.channels && (
              <p className="mt-1 text-sm text-red-600">{errors.channels}</p>
            )}
          </div>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={createCollection.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-collection-form"
            disabled={createCollection.isPending}
          >
            {createCollection.isPending ? 'Creating…' : 'Create Collection'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCollectionModal;
